import React from 'react'
import styled from 'styled-components'
import tw from 'twin.macro'
import PopLink from '@components/PopLink'
import { useAmp } from 'next/amp'
import { useTranslation } from 'react-i18next'
import {
  RecommendedArticles_recommendedArticles_articles_publisher,
  RecommendedArticles_recommendedArticles_articles_categories,
} from '@apollo-types/RecommendedArticles'
import { DesktopDisplayContainer } from '@components/DeviceContainer'
import PopImage from '@components/PopImage'
import { publicUrl } from '@libs/appUrl'

interface DesktopRecommendedArticleImage {
  url: string
  noShaper: boolean
}

interface DesktopRecommendedArticleCardProps {
  recommendedUrl: string
  title: string
  articleImage: DesktopRecommendedArticleImage
  publisher: RecommendedArticles_recommendedArticles_articles_publisher
  plainTextContent: string
  publishedTime: {
    formatted: string
    dateTime: string
  }
  trackEnterEvent: () => void
  categories: (RecommendedArticles_recommendedArticles_articles_categories | null)[] | null
}

const DesktopRecommendedArticleCard = (props: DesktopRecommendedArticleCardProps) => {
  const isAmp = useAmp()
  const { t } = useTranslation('post')
  const Img = isAmp ? 'amp-img' : 'img'

  const {
    recommendedUrl,
    title,
    articleImage,
    publisher,
    plainTextContent,
    publishedTime,
    trackEnterEvent,
    categories,
  } = props

  const isSecret = categories?.map((category) => category.name)?.includes('匿名聊')

  return (
    <DesktopDisplayContainer>
      <Container>
        <PopLink url={recommendedUrl} onClick={trackEnterEvent} shouldOpenInCurrentTab>
          <Title>{title}</Title>
        </PopLink>
        <ArticleImageContainer>
          <PopLink url={recommendedUrl} onClick={trackEnterEvent} shouldOpenInCurrentTab>
            {!isAmp ? (
              <ArticleImage>
                <StyledPopImage
                  noShaper={articleImage.noShaper}
                  imageUrl={articleImage.url}
                  sizes={`(min-width: 1216px) 750px, (min-width: 992px) 620px, (min-width: 768px) 690px, calc(100vw - 30px)`}
                  ratio={0.52}
                />
              </ArticleImage>
            ) : (
              <amp-img src={articleImage.url} width="100" height="52" layout="responsive" />
            )}
          </PopLink>
        </ArticleImageContainer>
        <PopLink
          url={recommendedUrl}
          onClick={trackEnterEvent}
          shouldOpenInCurrentTab
          disableOnClick={isSecret}
        />
        <PublisherContainer>
          <PopLink url={publicUrl(`/user/${publisher?.mid}`)} shouldOpenInCurrentTab={true}>
            <div className="photo-container">
              {publisher?.photoUrl && <Img src={publisher.photoUrl} width="40" height="40" />}
            </div>
            <div>
              <p className="user-name">{publisher?.name}</p>
              <p className="publish-time" data-datetime={publishedTime.dateTime}>
                {publishedTime.formatted}
              </p>
            </div>
          </PopLink>
        </PublisherContainer>
        <PopLink url={recommendedUrl} onClick={trackEnterEvent} shouldOpenInCurrentTab>
          <Content>{`${plainTextContent.substring(0, 150)}...`}</Content>
        </PopLink>
        <PopLink url={recommendedUrl} onClick={trackEnterEvent} shouldOpenInCurrentTab>
          <ReadMore>{t(['readMore', '閱讀更多...'])}</ReadMore>{' '}
        </PopLink>
      </Container>
    </DesktopDisplayContainer>
  )
}

const Container = styled.div`
  ${tw`w-full cursor-pointer px-0`}
`

const Title = styled.h2`
  ${tw`font-semibold text-h1 mb-4 hidden`}
  color: black;
  max-height: 76px;
  line-height: 1.35;
  /* the workaround for text-overflow: ellipsis; only works in 1 line text */
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  display: -webkit-box;
`

const PublisherContainer = styled.div`
  ${tw`hover:opacity-60 transition duration-500`}
  margin-bottom: 17px;
  a {
    ${tw`flex`}
  }
  img {
    ${tw`rounded-full`}
    width: 40px;
    height: 40px;
  }
  .photo-container {
    ${tw`mr-3`}
  }
  .user-name {
    ${tw`text-h5-d font-medium`}
    color: black;
  }
  .publish-time {
    ${tw`text-gray-600 text-c2-d block`}
  }
`

const ArticleImageContainer = styled.div`
  ${tw`mb-4`}
  img {
    ${tw`rounded-2 object-cover`}
  }
`

const ArticleImage = styled.div`
  img {
    ${tw`w-full h-full absolute top-0 bottom-0 right-0 left-0`}
  }
`

const StyledPopImage = styled(PopImage)`
  position: relative;
  /* 0.523: 圖片 高/寬 比例 (保持 寬/高 1.91) */
  padding-top: 52%;
`

const Content = styled.div`
  ${tw`text-gray-800 block mb-4`}
  font-size: 18px;
  line-height: 1.7;
`

const ReadMore = styled.p`
  ${tw`text-primary-indigo block font-semibold hover:text-primary-darkGrey transition duration-500`}
  font-size: 18px;
  margin-bottom: 40px;
`

export default DesktopRecommendedArticleCard
